"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"

/**
 * Build a stable, order-independent cache key for a list of productIds.
 * Non-mutating: never calls `.sort()` on the caller's array (which would be a bug,
 * since callers often pass memoized arrays that should stay stable).
 * `undefined` productIds (unfiltered / global mode) maps to a sentinel.
 */
export function productIdsCacheKey(productIds: number[] | undefined): string {
  if (productIds === undefined) return "__all__"
  return [...new Set(productIds)].sort((a, b) => a - b).join(",")
}

interface ProductCogsRecord {
  product_id: number
  variant_id: number | null
  cogs_per_unit: number
  source: string
  title: string
  sku: string | null
  variant_title: string | null
  notes: string | null
  updated_at: string
}

interface UseProductCogsOptions {
  /** COGS are store-scoped — the same product_id can carry a different cost per shop. */
  integrationId: string
  productIds?: number[]
  enabled?: boolean
}

interface UseProductCogsReturn {
  /** Map of product_id -> cogs_per_unit (product-level only) */
  cogsMap: Record<string, number>
  /** Full COGS records for the current productIds window (or all records in unfiltered mode) */
  records: ProductCogsRecord[]
  isLoading: boolean
  /** Upsert COGS for one or more products */
  updateCogs: (items: Array<{
    product_id: number
    cogs_per_unit: number
    title: string
    sku?: string | null
    variant_id?: number | null
    variant_title?: string | null
    notes?: string | null
  }>) => Promise<void>
  /** Delete COGS for a product */
  deleteCogs: (productId: number, variantId?: number | null) => Promise<void>
  /** Force a full refetch; drops cached entries. */
  reload: () => void
}

export function useProductCogs({
  integrationId,
  productIds,
  enabled = true,
}: UseProductCogsOptions): UseProductCogsReturn {
  const { fetchWithAuth } = useAuth()
  const fetchRef = useRef(fetchWithAuth)
  fetchRef.current = fetchWithAuth

  const integrationIdRef = useRef(integrationId)
  integrationIdRef.current = integrationId

  const [records, setRecords] = useState<ProductCogsRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  // Filtered mode (productIds defined): per-product cache, incremental fetch.
  // Empty array is a valid cache value (product has no COGS records).
  const cacheRef = useRef(new Map<number, ProductCogsRecord[]>())

  // The cache is keyed by product_id only, so it must be dropped when the store changes —
  // otherwise one shop's costs would surface for another.
  const cachedIntegrationRef = useRef(integrationId)

  const productIdsKey = useMemo(() => productIdsCacheKey(productIds), [productIds])

  const requestedIdsRef = useRef<number[] | undefined>(productIds)
  requestedIdsRef.current = productIds

  useEffect(() => {
    if (!enabled || !integrationId) return

    const cache = cacheRef.current
    if (cachedIntegrationRef.current !== integrationId) {
      cache.clear()
      cachedIntegrationRef.current = integrationId
    }
    const requested = requestedIdsRef.current

    // Assemble records for filtered mode from the cache (deduped by product_id:variant_id).
    const assembleFromCache = (ids: number[]): ProductCogsRecord[] => {
      const seen = new Map<string, ProductCogsRecord>()
      for (const id of ids) {
        const entries = cache.get(id)
        if (!entries) continue
        for (const r of entries) {
          seen.set(`${r.product_id}:${r.variant_id ?? ""}`, r)
        }
      }
      return Array.from(seen.values())
    }

    // --- Filtered mode ---
    if (requested !== undefined) {
      if (requested.length === 0) {
        setRecords([])
        setIsLoading(false)
        return
      }

      const needed: number[] = []
      for (const id of requested) {
        if (!cache.has(id)) needed.push(id)
      }

      if (needed.length === 0) {
        // Pure cache hit — e.g. product removed or reselected.
        setRecords(assembleFromCache(requested))
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      let cancelled = false

      void (async () => {
        try {
          const params = new URLSearchParams({
            integration_id: integrationId,
            product_ids: needed.join(","),
          })
          const res = await fetchRef.current(`/api/integrations/shopify/product-cogs?${params}`)
          if (!res.ok || cancelled) return
          const data = await res.json()
          const fetched: ProductCogsRecord[] = data.cogs ?? []

          // Group fetched records by product_id; fill empty arrays for ids without records.
          const byId = new Map<number, ProductCogsRecord[]>()
          for (const r of fetched) {
            const arr = byId.get(r.product_id)
            if (arr) arr.push(r)
            else byId.set(r.product_id, [r])
          }
          for (const id of needed) {
            cache.set(id, byId.get(id) ?? [])
          }

          if (!cancelled) setRecords(assembleFromCache(requested))
        } catch {
          // silently fail; keep existing records
        } finally {
          if (!cancelled) setIsLoading(false)
        }
      })()

      return () => { cancelled = true }
    }

    // --- Unfiltered (global) mode: fetch all records, no per-id cache ---
    let cancelled = false
    setIsLoading(true)
    void (async () => {
      try {
        const params = new URLSearchParams({ integration_id: integrationId })
        const res = await fetchRef.current(`/api/integrations/shopify/product-cogs?${params}`)
        if (res.ok && !cancelled) {
          const data = await res.json()
          setRecords(data.cogs ?? [])
        }
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, productIdsKey, reloadKey, integrationId])

  // cogsMap derived from the records state (still stable when records contents unchanged).
  const cogsMap = useMemo(() => {
    const map: Record<string, number> = {}
    for (const r of records) {
      if (r.variant_id == null) {
        map[String(r.product_id)] = r.cogs_per_unit
      }
    }
    return map
  }, [records])

  // Sync a set of records into the per-product cache (filtered mode only).
  const syncCache = useCallback((incoming: ProductCogsRecord[]) => {
    const cache = cacheRef.current
    const touched = new Map<number, ProductCogsRecord[]>()
    for (const r of incoming) {
      const existing = touched.get(r.product_id) ?? (cache.get(r.product_id) ? [...(cache.get(r.product_id) ?? [])] : [])
      const key = `${r.variant_id ?? ""}`
      const idx = existing.findIndex((e) => `${e.variant_id ?? ""}` === key)
      if (idx >= 0) existing[idx] = r
      else existing.push(r)
      touched.set(r.product_id, existing)
    }
    for (const [id, rows] of touched) cache.set(id, rows)
  }, [])

  const removeFromCache = useCallback((productId: number, variantId: number | null) => {
    const cache = cacheRef.current
    const entries = cache.get(productId)
    if (!entries) return
    const filtered = entries.filter((r) => (r.variant_id ?? null) !== variantId)
    cache.set(productId, filtered)
  }, [])

  const updateCogs = useCallback(
    async (items: Array<{
      product_id: number
      cogs_per_unit: number
      title: string
      sku?: string | null
      variant_id?: number | null
      variant_title?: string | null
      notes?: string | null
    }>) => {
      const body = items.length === 1 ? items[0] : { cogs: items }
      const params = new URLSearchParams({ integration_id: integrationIdRef.current })
      const res = await fetchRef.current(`/api/integrations/shopify/product-cogs?${params}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error ?? "Failed to update COGS")
      }

      const { cogs } = await res.json() as { cogs: ProductCogsRecord[] }

      // Mirror server response into both the cache (source of truth for filtered mode)
      // and the records state (so current view updates immediately).
      syncCache(cogs)
      setRecords((prev) => {
        const map = new Map(prev.map((r) => [`${r.product_id}:${r.variant_id ?? ""}`, r]))
        for (const c of cogs) {
          map.set(`${c.product_id}:${c.variant_id ?? ""}`, c)
        }
        return Array.from(map.values())
      })
    },
    [syncCache],
  )

  const deleteCogs = useCallback(
    async (productId: number, variantId?: number | null) => {
      const params = new URLSearchParams({
        integration_id: integrationIdRef.current,
        product_id: String(productId),
      })
      if (variantId != null) params.set("variant_id", String(variantId))

      const res = await fetchRef.current(`/api/integrations/shopify/product-cogs?${params}`, {
        method: "DELETE",
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error ?? "Failed to delete COGS")
      }

      removeFromCache(productId, variantId ?? null)
      setRecords((prev) =>
        prev.filter((r) => !(r.product_id === productId && r.variant_id === (variantId ?? null))),
      )
    },
    [removeFromCache],
  )

  const reload = useCallback(() => {
    cacheRef.current.clear()
    setReloadKey((k) => k + 1)
  }, [])

  return { cogsMap, records, isLoading, updateCogs, deleteCogs, reload }
}
